import _ from 'lodash';
import { createSelector } from 'reselect';

// Base selectors
const getWeatherConditionsState = state => state.weatherConditions || {};

/** Matches urban/app/controllers/campaigns/weather-conditions.js QUALIFIER_OPTIONS */
export const QUALIFIER_LABEL_FALLBACK = {
    equal: 'equals to',
    greaterThan: 'greater than',
    lessThan: 'less than',
    greaterThanInclusive: 'equal to or greater than',
    lessThanInclusive: 'equal to or less than',
};

/** Matches urban/app/controllers/campaigns/weather-conditions.js TIMEFRAME_OPTIONS */
export const TIMEFRAME_LABEL_FALLBACK = {
    now: 'Now',
    next_36h: 'Next 36 hours',
    next_14d: 'Next 14 days',
    next_3d: 'Next 3 Days',
    next_6d: 'Next 6 Days',
    forecast: 'Forecast',
};

const TEMPERATURE_WEATHER_TYPES = ['Temperature', 'FeelsLike', 'humidity', 'windspeed'];

// Simple selectors
export const getWeatherConditions = state =>
    getWeatherConditionsState(state).weatherConditions || [];
export const getWeatherConstants = state => getWeatherConditionsState(state).constants || {};
export const getWeatherEnvironment = state => getWeatherConditionsState(state).environment || 'ca';
export const getWeatherConditionsLoading = state =>
    getWeatherConditionsState(state).loading || false;
export const getWeatherConditionsError = state => getWeatherConditionsState(state).error;

// Utility function selectors (replaces the constants file functions)
export const getWeatherConditionOptions = createSelector(
    [getWeatherConditions, getWeatherEnvironment],
    (weatherConditions, environment) => conditionsToUse => {
        return _.filter(
            weatherConditions,
            setting =>
                _.includes(conditionsToUse, setting.value) && _.includes(setting.env, environment)
        );
    }
);

export const getConditionLabel = createSelector(
    [getWeatherConditions, getWeatherEnvironment],
    (weatherConditions, environment) => type => {
        return _.find(
            weatherConditions,
            condition => condition.value === type && _.includes(condition.env, environment)
        )?.label;
    }
);

export const getQualifierLabel = createSelector(
    [getWeatherConstants],
    constants => qualifier => {
        if (qualifier === '') {
            return '';
        }
        const fromApi = constants.qualifierOptions?.find(option => option.value === qualifier)
            ?.label;
        return fromApi ?? QUALIFIER_LABEL_FALLBACK[qualifier] ?? qualifier;
    }
);

export const getValueLabel = createSelector(
    [getWeatherConstants],
    constants => (value, type) => {
        // Try to find by numeric value first, then by alias
        const byValue = _(constants.valueOptions)
            .filter(option => _.includes(option.types, type))
            .find(option => option.value === value);

        if (byValue) return byValue.label;

        // Try to find by alias for string values
        const byAlias = _(constants.valueOptions)
            .filter(option => _.includes(option.types, type))
            .find(option => option.alias === value);

        return byAlias?.label;
    }
);

export const getTimeframeLabel = createSelector(
    [getWeatherConstants],
    constants => value => {
        if (!value) {
            return '';
        }
        const fromApi = constants.timeframeOptions?.find(option => option.value === value)?.label;
        return fromApi ?? TIMEFRAME_LABEL_FALLBACK[value] ?? value;
    }
);

export const getIsWeatherTypeTemperature = createSelector(
    [getWeatherConstants],
    () => type => {
        return TEMPERATURE_WEATHER_TYPES.includes(type);
    }
);

/**
 * Human-readable weather condition value for summaries (categorical label, numeric + unit, or raw).
 *
 * @param {number|string} amount - Stored condition amount
 * @param {string} type - Weather condition type key
 * @param {Object} options
 * @param {Function} [options.getValueLabel] - From getValueLabel selector
 * @param {Record<string, string>} [options.inputAdornmentMap] - Unit suffixes by type
 * @param {(type: string) => boolean} [options.isWeatherTypeTemperature] - From getIsWeatherTypeTemperature
 * @returns {string}
 */
export function formatWeatherConditionValue(amount, type, options = {}) {
    const { getValueLabel, inputAdornmentMap = {}, isWeatherTypeTemperature } = options;

    if (amount === -1 || amount === '' || amount === null || amount === undefined) {
        return '';
    }

    const valueLabel = getValueLabel?.(amount, type);
    if (valueLabel) {
        return valueLabel;
    }

    const isTemperatureType = isWeatherTypeTemperature
        ? isWeatherTypeTemperature(type)
        : TEMPERATURE_WEATHER_TYPES.includes(type);

    if (isTemperatureType) {
        const unit = inputAdornmentMap[type] || '';
        return `${amount} ${unit}`.trim();
    }

    return String(amount);
}

/**
 * @param {Object} condition - Weather condition with type, qualifier, amount, timeframe
 * @param {Object} weatherConditionsData - Bound selector fns from Redux
 * @returns {{ conditionLabel: string, qualifierLabel: string, valueLabel: string, timeframeLabel: string }}
 */
export function getWeatherConditionFieldLabels(condition, weatherConditionsData) {
    const { type, qualifier, amount, timeframe } = condition;

    const conditionLabel = weatherConditionsData?.getConditionLabel?.(type) || type || '';
    const qualifierLabel =
        weatherConditionsData?.getQualifierLabel?.(qualifier) ||
        QUALIFIER_LABEL_FALLBACK[qualifier] ||
        qualifier ||
        '';
    const valueLabel = formatWeatherConditionValue(amount, type, {
        getValueLabel: weatherConditionsData?.getValueLabel,
        inputAdornmentMap: weatherConditionsData?.inputAdornmentMap,
        isWeatherTypeTemperature: weatherConditionsData?.isWeatherTypeTemperature,
    });
    const timeframeLabel =
        weatherConditionsData?.getTimeframeLabel?.(timeframe) ||
        TIMEFRAME_LABEL_FALLBACK[timeframe] ||
        timeframe ||
        '';

    return { conditionLabel, qualifierLabel, valueLabel, timeframeLabel };
}

export const getHasWeatherConditions = () => weatherConditions => {
    return (
        weatherConditions && weatherConditions.conditions && weatherConditions.conditions.length > 0
    );
};

// For easy access to specific constants
export const getMaxNumberOfConditions = createSelector(
    [getWeatherConstants],
    constants => constants.maxNumberOfConditions
);

export const getInputAdornmentMap = createSelector(
    [getWeatherConstants],
    constants => constants.inputAdornmentMap
);

export const getValidWeatherConditionMultipleOptionType = createSelector(
    [getWeatherConstants],
    constants => constants.VALID_WEATHER_CONDITION_MULTIPLE_OPTION_TYPE
);

export const getValidWeatherConditionQualifier = createSelector(
    [getWeatherConstants],
    constants => constants.VALID_WEATHER_CONDITION_QUALIFIER
);

export const getValidWeatherConditionType = createSelector(
    [getWeatherConstants],
    constants => constants.VALID_WEATHER_CONDITION_TYPE
);

export const getValidWeatherConditionTimeframe = createSelector(
    [getWeatherConstants],
    constants => constants.VALID_WEATHER_CONDITION_TIMEFRAME
);
